import React, { useCallback, useEffect, useState } from 'react'
import * as yup from 'yup'
import * as cpf from '@fnando/cpf'
import { useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { InputAdornment, CircularProgress, Link, Box } from '@material-ui/core'
import { useApi } from '~/store/store'
import { OnboardingWizardActions, trackEvent } from '~/service/analytics'
import { CreateAccountFormValues, useCreateAccountFormStore } from '~/store/createAccountForm'
import { OnboardingFormLayout, HfNameInput, HfNumberMaskedInput, HfTextInput, HfUsernameInput } from '~/components'
import { TermsCheckbox } from './components'
import { formatEmail, getPartnerFromURLQuery } from '~/utils/format'
import { emailValidation } from '~/utils/masks'

enum TermsTypes { MUTUAL = 'mutual', SCR = 'scr', CERC = 'cerc' }

const validationSchema = yup.object().shape({
  companyEmail: yup.string().required('Campo obrigatório')
    .matches(emailValidation, 'Email inválido')
    .email('Email inválido'),
  responsibleCpf: yup.string().required('Campo obrigatório')
    .test('If username is a valid CPF', 'CPF inválido',
      username => typeof username === 'string' ? cpf.isValid(username) : false),
  responsibleName: yup.string().required('Campo obrigatório')
    .matches(/^[A-zÀ-ú].+\s.+[A-zÀ-ú ]+(\s)?$/, 'Lembre-se de inserir o seu nome completo')
    .test('If has incorret characteres', 'Favor não utilizar caractéres especiais', name => {
      const unallowedCharFormat = '!#$%&()*+./\\:;<=>?@[]^_`{|}~0123456789"'.split('')
      const unallowedChars = name?.split('').filter(char => unallowedCharFormat.includes(char))

      if (unallowedChars && unallowedChars.length !== 0) {
        return false
      }
      return true
    }),
  phone: yup.string().required('Campo obrigatório').min(15, 'Número de telefone inválido')
})

export const BaseAccountData: React.FC = () => {
  const [acceptMutualTerms, setAcceptMutualTerms] = useState(false)
  const [acceptSCRTerms, setAcceptSCRTerms] = useState(false)
  const [acceptCERCTerms, setAcceptCERCTerms] = useState(false)
  const { createAccountFormState, updateForm } = useCreateAccountFormStore()
  const [checkIfAccountExists, checkIfAccountExistsState] = useApi('checkIfAccountExists')
  const { form } = createAccountFormState
  const { loading } = checkIfAccountExistsState
  const history = useHistory()
  const location = useLocation()

  const { handleSubmit, control, formState: { errors }, setError, watch, clearErrors } = useForm<CreateAccountFormValues>({
    defaultValues: form,
    resolver: yupResolver(validationSchema)
  })
  const email = watch('companyEmail')

  useEffect(() => {
    clearErrors()
  }, [email])

  const handleAcceptTerms = useCallback((termsType: TermsTypes) => {
    if (termsType === TermsTypes.MUTUAL) {
      setAcceptMutualTerms(!acceptMutualTerms)
      return
    }
    if (termsType === TermsTypes.CERC) {
      setAcceptCERCTerms(!acceptCERCTerms)
      return
    }

    setAcceptSCRTerms(!acceptSCRTerms)
  }, [setAcceptMutualTerms, acceptMutualTerms, setAcceptSCRTerms, acceptSCRTerms, acceptCERCTerms, setAcceptCERCTerms])

  const onSubmit = useCallback(async (data: CreateAccountFormValues) => {
    const existingEmailOrPhone = await checkIfAccountExists({ phone: data.phone })
    if (existingEmailOrPhone.error) {
      if (existingEmailOrPhone.error.code === 'PHONE_ALREADY_EXISTS') {
        setError('phone', { type: 'manual', message: 'Telefone já cadastrado na plataforma' })
        trackEvent(OnboardingWizardActions.ON_FIRST_PAGE_NEXT_CLICK_UNSUCCESS)
        return
      }
    }

    const hasResponsibleAccount = await checkIfAccountExists({ cpf: cpf.strip(data.responsibleCpf) })
    const partner = getPartnerFromURLQuery(location.search)

    const updatedData = {
      ...data,
      origin: partner || data.origin,
      hasMutualAccount: hasResponsibleAccount.data || false
    }

    trackEvent(OnboardingWizardActions.ON_FIRST_PAGE_NEXT_CLICK_SUCCESS)
    trackEvent(OnboardingWizardActions.ACCEPT_TERMS)
    updateForm(updatedData)
    history.push('/register/company-info')
  }, [checkIfAccountExists, updateForm, history, setError])

  const verifyEmail = useCallback(async () => {
    if (email.match(emailValidation)) {
      const existingEmailOrPhone = await checkIfAccountExists({ email: formatEmail(email) })
      if (existingEmailOrPhone.error && existingEmailOrPhone.error.code === 'EMAIL_ALREADY_EXISTS') {
        setError('companyEmail', { type: 'manual', message: 'E-mail já cadastrado na plataforma' })
        return
      }
      return
    }

    setError('companyEmail', { type: 'manual', message: 'E-mail inválido' })
  }, [email, checkIfAccountExists, setError])

  return (
    <OnboardingFormLayout
      title='Solicite um empréstimo para sua empresa'
      subtitle='Para começar, informe os seus dados abaixo:'
      canContinue={!loading && acceptMutualTerms && acceptSCRTerms && acceptCERCTerms && !loading && !errors.companyEmail}
      removeBackButton
      onNextClick={handleSubmit(onSubmit)}
    >
      <HfTextInput
        label='E-mail'
        control={control}
        name='companyEmail'
        inputMode='email'
        onBlur={verifyEmail}
        autoCapitalize='off'
        errorMessage={errors.companyEmail?.message}
        error={errors.companyEmail?.message}
        placeholder='financeiro@suaempresa.com.br'
        subtitle='Este e-mail será utilizado para envio de comunicações da Mutual e boletos do empréstimo.'
        endAdornment={(
          <InputAdornment position='end'>
            {loading && (
              <CircularProgress color='primary' size={20} />
            )}
          </InputAdornment>
        )}
      />

      <HfNameInput
        label='Seu nome'
        name='responsibleName'
        control={control}
        placeholder='Nome Sobrenome'
        errorMessage={errors.responsibleName?.message}
        error={errors.responsibleName?.message}
      />

      <HfUsernameInput
        acceptCPF
        label='CPF'
        name='responsibleCpf'
        control={control}
        error={errors.responsibleCpf?.message}
        errorMessage={errors.responsibleCpf?.message}
        placeholder='999.999.999-99'
      />

      <HfNumberMaskedInput
        formatted
        mask='(##) #####-####'
        label='Telefone'
        name='phone'
        control={control}
        error={errors.phone?.message}
        errorMessage={errors.phone?.message}
        placeholder='(99) 99999-9999'
      />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TermsCheckbox
          name='acceptTerms'
          checked={acceptMutualTerms}
          onChange={() => handleAcceptTerms(TermsTypes.MUTUAL)}
          description={(
            <>
              Declaro que li e aceito os {''}
              <Link
                href='https://mutual.club/Mutual.Termos.e.condicoes.Tomadores.PJ.html'
                target='blank'
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  color: 'blue.main',
                  textDecoration: 'underline'
                }}
              >
                Termos de Uso
              </Link>
              {' '}e a{' '}
              <Link
                href='https://mutual.club/Mutual.Termos.Gerais.e.Condicoes.de.uso.html'
                target='blank'
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  color: 'blue.main',
                  textDecoration: 'underline'
                }}
              >
                Política de Privacidade
              </Link>.
            </>
          )}
        />

        <TermsCheckbox
          name='acceptSCRTerms'
          checked={acceptSCRTerms}
          onChange={() => handleAcceptTerms(TermsTypes.SCR)}
          description='Autorizo o uso dos meus dados e da minha empresa para consulta no Sistema de Informações de Crédito (SCR)'
        />

        <TermsCheckbox
          name='acceptCERCTerms'
          checked={acceptCERCTerms}
          onChange={() => handleAcceptTerms(TermsTypes.CERC)}
          description=' Autorizo o uso dos dados da minha empresa para consulta na Central de Recebíveis (CERC)'
        />
      </Box>
    </OnboardingFormLayout>
  )
}
